/**
 * Where an interaction is allowed to render.
 *
 * A published document is public JSON on a CDN with a permissive CORS header, so anybody can lift
 * the snippet out of a publisher's article, drop it on their own site, and the interaction appears.
 * Only writes are origin checked, so the votes are refused and the copy quietly records nothing: it
 * looks like it works, it inflates the publisher's view count, and it collects no data. The
 * published document carries the domains it belongs on, and the widget renders nowhere else.
 *
 * This is not a security boundary and cannot be one. The check runs in the reader's browser and
 * anybody can edit what runs there. `originAllowed` in `services/api/src/reader.ts` is the
 * boundary, and it is the one that decides whether an event is kept. This check is what stops an
 * honest copy from looking like it works.
 */

/**
 * The domain list as the widget reads it.
 *
 * Absent means render, and that rule matters more than anything else here: every document already
 * on the CDN was published before this field existed, so reading "no list" as "nowhere" would take
 * working embeds off real articles the moment it shipped. A list that is present but unreadable
 * falls back the same way. A document the widget cannot interpret is a bug of ours, and a blank
 * space in somebody's article is the worst possible place to discover it.
 */
export function readDomains(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  const domains: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    /**
     * The same shape `normaliseDomain` in @quizee/core stores: labels only, lower case, no leading
     * dot and no wildcard. Entries arrive already in that shape, so this is only here so that a
     * document written by hand, or one written before normalisation existed, matches what a
     * publisher meant rather than matching nothing at all.
     */
    const domain = entry
      .trim()
      .toLowerCase()
      .replace(/^\*?\./, '')
      .replace(/\.$/, '');
    if (domain.length > 0 && !domains.includes(domain)) domains.push(domain);
  }

  // Nothing usable is the same answer as nothing at all: render. It is also what `originAllowed`
  // does with an empty allowlist, so the two ends of the pipe agree.
  return domains.length === 0 ? undefined : domains;
}

/**
 * A developer's own machine, allowed whatever the list says.
 *
 * The same rule and the same three hosts as `isLocalOrigin` in `services/api/src/reader.ts`.
 * Somebody integrating the embed works locally long before their production domain is on the
 * brand, and making them wait for an owner to add it is a support conversation for no benefit.
 * `[::1]` is what a browser puts in `location.hostname` for the IPv6 loopback, and the brackets are
 * already gone by the time this is called.
 */
function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

/**
 * May a page on this hostname render an interaction published for these domains.
 *
 * A domain covers itself and every subdomain of it, because that is what a publisher means when
 * they type `herald.co.uk`. The dot in front is the whole of the difference between that and a
 * plain suffix match: `notherald.co.uk` ends with `herald.co.uk` and belongs to somebody else.
 */
export function hostAllowed(hostname: string, domains: readonly string[] | undefined): boolean {
  if (domains === undefined || domains.length === 0) return true;

  const host = hostname
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '')
    .replace(/\.$/, '');
  // No hostname at all is a `file://` page or an `about:blank` frame. Neither is a domain the
  // publisher listed, and the list says where this may run.
  if (host.length === 0) return false;
  if (isLocalHost(host)) return true;

  return domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}
